import { cloneElement, useCallback, useEffect, useState } from "react";
import { Size } from "@hokuto/jam-core";
import { JamCanvasProps, JamDrawerBaseParams } from "../types.js";
import { useControls } from "../hooks/controls.js";
import { useCanvas, getChildrenParams } from "../hooks/canvas.js";
import { debounce } from "../utils/debounce.js";
import { resizeCanvas } from "../utils/canvas.js";
import { ANIMATION_DURATION, TILE_SIZE } from "../const.js";
import { StyledJamImage } from "./styled.js";

export function JamCanvas(args: JamCanvasProps) {
  const {
    maxZoom,
    minZoom,
    init,
    paramsObserver,
    children,
    style = {},
    width: WIDTH = TILE_SIZE,
    height: HEIGHT = TILE_SIZE,
    duration = ANIMATION_DURATION,
    ...props
  } = args;

  const currentSize = {
    width: Math.floor(WIDTH),
    height: Math.floor(HEIGHT),
  };

  const [canvas, setCanvas] = useState<HTMLCanvasElement>();

  const {
    baseProps,
    tilesProps,
    canvasStatus,
    atlasParams,
    mapParams,
    setChildrenParams,
  } = useCanvas({ ...props, ...currentSize, paramsObserver });

  const childrenArr = children instanceof Array ? children : [children];
  const { controls, drawersObservable, transformation } = useControls({
    ...currentSize,
    setChildrenParams,
    paramsObserver,
    canvas,
    duration,
    maxZoom,
    minZoom,
    init,
    bgColor: baseProps.bgColor,
    grabCursor: style.cursor,
  });

  const changeCallback = useCallback(
    function (currentSize: Size) {
      requestAnimationFrame(() => {
        if (!controls) return;
        const { width: dw, height: dh } =
          controls.element.getBoundingClientRect();

        resizeCanvas({
          canvas: controls.element,
          ...currentSize,
          dw,
          dh,
        });

        if (!transformation) return;

        const childrenParams = getChildrenParams({
          controls,
          transformation,
        });
        setChildrenParams(childrenParams);
      });
    },
    [controls, transformation]
  );

  const changeHandler = useCallback(debounce(changeCallback, duration), [
    changeCallback,
    duration,
  ]);

  useEffect(() => {
    changeCallback(currentSize);
  }, [changeCallback]);

  useEffect(() => {
    changeHandler(currentSize);
  }, [currentSize.width, currentSize.height]);

  const canvasRef = useCallback(
    (element: HTMLCanvasElement | null) => {
      element && canvas !== element && setCanvas(element);
    },
    [canvas]
  );

  const onError = useCallback(
    (args: JamDrawerBaseParams) => {
      if (!paramsObserver) return;
      return function (error: Error) {
        const { cause = {} } = error;
        error.cause = {
          ...(cause instanceof Object ? cause : { cause }),
          ...args,
        };
        paramsObserver.sendError(error);
      };
    },
    [paramsObserver]
  );
  return (
    <>
      {controls &&
        canvasStatus &&
        tilesProps &&
        childrenArr.map((element, drawerIndex) => {
          return cloneElement(element, {
            key: drawerIndex,
            drawerIndex,
            controls,
            mapParams,
            atlasParams,
            canvasStatus,
            tilesProps,
            drawersObservable,
            paramsObserver,
            onError: onError({
              canvasStatus,
              controls,
              drawerIndex,
              drawersObservable,
              tilesProps,
              atlasParams,
              mapParams,
            }),
          });
        })}

      <StyledJamImage
        ref={canvasRef}
        as={"canvas"}
        style={{ touchAction: "none", ...style }}
      />
    </>
  );
}
